import React from 'react';
import { Link } from 'react-router-dom';
import ReactPlayer from 'react-player';
import '../../shared/block-content.css';
import styles from './BlockRenderer.module.scss';

function isInternalUrl(url) {
    if (!url) return false;
    if (url.startsWith('/')) return true;
    try {
        const parsed = new URL(url, window.location.origin);
        return parsed.origin === window.location.origin;
    } catch { return false; }
}

function parseContent(content) {
    if (!content) return [];
    try {
        const parsed = JSON.parse(content);
        if (parsed && parsed.version === 1 && Array.isArray(parsed.blocks)) {
            return parsed.blocks;
        }
    } catch {
        // Not JSON — render legacy HTML directly
        if (content.trim()) {
            return [{ id: 'legacy', type: 'raw-html', data: { html: content } }];
        }
    }
    return [];
}

function renderBlock(block, index) {
    const { type, data } = block;
    const key = block.id || index;

    switch (type) {
        case 'heading': {
            const level = data.level || 2;
            const Tag = `h${Math.min(Math.max(level, 2), 6)}`;
            return <Tag key={key} className="bc-heading" dangerouslySetInnerHTML={{ __html: data.text }} />;
        }

        case 'paragraph':
            return <p key={key} className="bc-paragraph" dangerouslySetInnerHTML={{ __html: data.text }} />;

        case 'image':
            return (
                <figure key={key} className="bc-figure">
                    <img src={data.url} alt={data.alt || ''} />
                    {data.caption && <figcaption className="bc-caption">{data.caption}</figcaption>}
                </figure>
            );

        case 'video':
            return (
                <figure key={key} className={`bc-video ${styles.videoBlock}`}>
                    <div className="bc-video-wrapper">
                        <ReactPlayer url={data.url} controls width="100%" height="100%" />
                    </div>
                    {data.caption && <figcaption className="bc-caption">{data.caption}</figcaption>}
                </figure>
            );

        case 'quote':
            return (
                <blockquote key={key} className="bc-quote">
                    <p dangerouslySetInnerHTML={{ __html: data.text }} />
                    {data.attribution && <cite>{data.attribution}</cite>}
                </blockquote>
            );

        case 'callout':
            return (
                <div key={key} className={`bc-callout ${data.style === 'warning' ? 'bc-callout-warning' : 'bc-callout-info'}`}>
                    <p dangerouslySetInnerHTML={{ __html: data.text }} />
                </div>
            );

        case 'list': {
            const Tag = data.style === 'ordered' ? 'ol' : 'ul';
            const items = data.items || [];
            return (
                <Tag key={key} className="bc-list">
                    {items.map((item, i) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                </Tag>
            );
        }

        case 'button': {
            const btnClass = `bc-button ${data.style === 'secondary' ? 'bc-button-secondary' : ''}`;
            if (data.openInNewTab) {
                return (
                    <div key={key} className="bc-button-block">
                        <a href={data.url} className={btnClass} target="_blank" rel="noopener noreferrer">
                            {data.text}
                        </a>
                    </div>
                );
            }
            if (isInternalUrl(data.url)) {
                const path = data.url.startsWith('/') ? data.url : new URL(data.url).pathname;
                return (
                    <div key={key} className="bc-button-block">
                        <Link to={path} className={btnClass}>{data.text}</Link>
                    </div>
                );
            }
            return (
                <div key={key} className="bc-button-block">
                    <a href={data.url} className={btnClass}>{data.text}</a>
                </div>
            );
        }

        case 'divider':
            return <hr key={key} className="bc-divider" />;

        case 'raw-html':
            return <div key={key} dangerouslySetInnerHTML={{ __html: data.html }} />;

        default:
            return null;
    }
}

const BlockRenderer = ({ content }) => {
    const blocks = parseContent(content);
    if (!blocks.length) return null;

    return (
        <div className="bc-content bc-theme-dark">
            {blocks.map((block, i) => (
                <div key={block.id || i} className="bc-block">
                    {renderBlock(block, i)}
                </div>
            ))}
        </div>
    );
};

export default BlockRenderer;
